import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daf7screp053ijub1km0";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeContact() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [msgCount, setMsgCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 防垃圾字段命中：不真正提交，但对外表现与正常成功一致
    const honeypot = String(fd.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，我们已经收到你的反馈啦！");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (!value) return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setMessage("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("提交成功，我们会尽快与你联系，敬请期待最新热点提醒！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setMessage("网络开小差了，请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-24" aria-label="订阅热点提醒与投稿">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* contact info */}
        <div className="flex flex-col justify-between gap-6 rounded-2xl bg-accent-600 p-6 md:p-8 lg:col-span-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-mail-send-line" />
              投稿与合作
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50">
              有独家线索或建议？
              <br />
              来和我们聊聊
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-accent-100">
              无论是重大进展的现场线索、值得关注的话题，还是对我们的建议，都欢迎通过左侧表单或下方方式找到我们。
              我们会在核实后第一时间跟进。
            </p>
          </div>

          <ul className="space-y-3 text-sm text-accent-50">
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50/15">
                <i className="ri-mail-fill" />
              </span>
              <span className="break-all">hello@chiigua.example.com</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50/15">
                <i className="ri-time-fill" />
              </span>
              <span>在线回应：工作日 09:00 – 21:00</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50/15">
                <i className="ri-community-fill" />
              </span>
              <span>全网热点 · 持续跟进 · 欢迎提供线索</span>
            </li>
          </ul>

          <div className="flex items-center gap-2 text-accent-100">
            <span className="text-xs text-accent-50">分享我们：</span>
            {[
              { icon: "ri-wechat-line", label: "微信" },
              { icon: "ri-weibo-line", label: "微博" },
              { icon: "ri-bilibili-line", label: "哔哩哔哩" },
              { icon: "ri-tiktok-line", label: "抖音" },
            ].map((s) => (
              <a
                key={s.label}
                href="#about"
                aria-label={s.label}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/15 text-sm transition-colors hover:bg-background-50/30"
              >
                <i className={s.icon} />
              </a>
            ))}
          </div>
        </div>

        {/* form */}
        <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-8 lg:col-span-3">
          <h3 className="font-heading text-xl font-bold text-foreground-900">订阅热点提醒</h3>
          <p className="mt-1.5 text-sm text-foreground-500">
            留下你的称呼与邮箱，重大热点更新与专题精选将第一时间推送到你手中。
          </p>

          <form
            id="newsletterForm"
            data-readdy-form={FORM_URL}
            onSubmit={handleSubmit}
            className="mt-6 space-y-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="sub-name" className="mb-1.5 block text-xs font-medium text-foreground-600">
                  你的称呼
                </label>
                <input
                  id="sub-name"
                  name="name"
                  type="text"
                  placeholder="怎么称呼你"
                  autoComplete="name"
                  className="w-full rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                />
              </div>
              <div>
                <label htmlFor="sub-email" className="mb-1.5 block text-xs font-medium text-foreground-600">
                  常用邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                />
              </div>
            </div>

            <div>
              <label htmlFor="sub-msg" className="mb-1.5 block text-xs font-medium text-foreground-600">
                投稿线索 / 想说的话
              </label>
              <textarea
                id="sub-msg"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="如果你有值得关注的线索或建议，请在这里描述，我们会在核实后跟进…"
                onChange={(e) => setMsgCount(e.target.value.length)}
                className="w-full resize-none rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
              <p className="mt-1 text-right text-[11px] text-foreground-400">
                <span id="msg-count">{msgCount}</span> / 500
              </p>
            </div>

            {/* anti-bot honeypot */}
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="aux-field-hidden"
            />

            {status === "error" && (
              <p className="rounded-md bg-secondary-100 px-3 py-2 text-sm text-secondary-900" role="alert">
                <i className="ri-alert-line mr-1.5" />
                {message}
              </p>
            )}
            {status === "success" && (
              <p className="rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900" role="status">
                <i className="ri-checkbox-circle-fill mr-1.5" />
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  正在提交…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" />
                  订阅热点提醒
                </>
              )}
            </button>
            <p className="text-[11px] text-foreground-400">
              提交即代表你同意我们仅将信息用于热点推送与内容反馈，绝不对第三方共享。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}